import { useMemo } from 'react'
import { Zoning } from '@popety-io/types'
import {
  getPopulatedMetrics,
  getExclusiveGroups,
} from '../../utils/zoningHelpers'
import { METRIC_DEFINITIONS } from '../../utils/zoningConstants'
import { MetricData } from '../utils/metricTableHelpers'

/** Derived surface metric keys that should only appear as DerivedSurfaceRow components */
const DERIVED_SURFACE_KEYS = ['vbr', 'sbd', 'sb', 'spd', 'sbpu', 'sp']

/** Metrics displayed in the header, not the table */
const HEADER_METRICS = ['construction_arrangement']

interface MetricCategories {
  indices: {
    occupation: MetricData[]
    utilization: MetricData[]
  }
  heights: MetricData[]
  floors: MetricData[]
  setbacks: MetricData[]
  usageRatios: MetricData[]
  environmental: MetricData[]
  other: MetricData[]
}

interface AllPossibleExclusiveMetrics {
  occupation: MetricData[]
  utilization: MetricData[]
}

interface AllPossibleNonExclusiveMetrics {
  heights: MetricData[]
  floors: MetricData[]
  setbacks: MetricData[]
  usage: MetricData[]
  environmental: MetricData[]
  other: MetricData[]
}

interface UseMetricCategoriesProps {
  zone: Zoning
  /** Saved overrides - used to include overridden metrics in view mode even if zone has no original data */
  overrides?: Record<string, number>
}

interface UseMetricCategoriesReturn {
  /** All populated metrics from the zone */
  allMetrics: MetricData[]
  /** Whether there are any metrics to display */
  hasMetrics: boolean
  /** Metrics organized by category (for view mode) */
  metricsByCategory: MetricCategories
  /** All possible exclusive group metrics (for edit mode) */
  allPossibleMetrics: AllPossibleExclusiveMetrics
  /** All possible non-exclusive metrics (for edit mode) */
  allPossibleNonExclusiveMetrics: AllPossibleNonExclusiveMetrics
}

/**
 * Creates a metric object from a key, using existing metric data if available
 */
const createMetricObject = (
  key: string,
  allMetrics: MetricData[]
): MetricData => {
  const existingMetric = allMetrics.find((m) => m.key === key)
  return (
    existingMetric || {
      key,
      min: undefined,
      max: undefined,
      minSource: undefined,
      maxSource: undefined,
      minReference: undefined,
      maxReference: undefined,
    }
  )
}

/**
 * Hook for organizing and categorizing zoning metrics
 * Provides metrics organized by category for view mode and all possible metrics for edit mode
 */
export const useMetricCategories = ({
  zone,
  overrides = {},
}: UseMetricCategoriesProps): UseMetricCategoriesReturn => {
  // Get populated metrics from zone
  const allMetrics = useMemo(
    () => getPopulatedMetrics(zone) as MetricData[],
    [zone]
  )

  const hasMetrics = allMetrics.length > 0

  // Get metric keys that have overrides but no original zone data
  const overriddenKeys = useMemo(() => {
    const populatedKeys = new Set(allMetrics.map((m) => m.key))
    const keys: string[] = []
    for (const overrideKey of Object.keys(overrides)) {
      // Override keys are like "ios_max", "green_ratio_max", "ios_min" - strip the suffix
      const metricKey = overrideKey.replace(/_(max|min)$/, '')
      if (
        !populatedKeys.has(metricKey) &&
        METRIC_DEFINITIONS[metricKey] &&
        !keys.includes(metricKey)
      ) {
        keys.push(metricKey)
      }
    }
    return keys
  }, [allMetrics, overrides])

  // Organize metrics by category
  const metricsByCategory = useMemo((): MetricCategories => {
    const categories: MetricCategories = {
      indices: {
        occupation: [],
        utilization: [],
      },
      heights: [],
      floors: [],
      setbacks: [],
      usageRatios: [],
      environmental: [],
      other: [],
    }

    const exclusiveGroups = getExclusiveGroups()

    const categorizeMetric = (metric: MetricData) => {
      const definition = METRIC_DEFINITIONS[metric.key]
      if (!definition) return

      // Skip derived surface metrics and header-only metrics
      if (DERIVED_SURFACE_KEYS.includes(metric.key)) return
      if (HEADER_METRICS.includes(metric.key)) return

      if (exclusiveGroups.group1.includes(metric.key)) {
        categories.indices.occupation.push(metric)
      } else if (exclusiveGroups.group2.includes(metric.key)) {
        categories.indices.utilization.push(metric)
      } else if (definition.category === 'height') {
        categories.heights.push(metric)
      } else if (definition.category === 'floor') {
        categories.floors.push(metric)
      } else if (definition.category === 'setback') {
        categories.setbacks.push(metric)
      } else if (definition.category === 'usage') {
        categories.usageRatios.push(metric)
      } else if (definition.category === 'environmental') {
        categories.environmental.push(metric)
      } else {
        categories.other.push(metric)
      }
    }

    allMetrics.forEach(categorizeMetric)

    overriddenKeys.forEach((key) => {
      categorizeMetric(createMetricObject(key, allMetrics))
    })

    return categories
  }, [allMetrics, overriddenKeys])

  // Get ALL possible metrics for exclusive groups (for edit mode)
  const allPossibleMetrics = useMemo((): AllPossibleExclusiveMetrics => {
    const exclusiveGroups = getExclusiveGroups()

    return {
      occupation: exclusiveGroups.group1.map((key) =>
        createMetricObject(key, allMetrics)
      ),
      utilization: exclusiveGroups.group2.map((key) =>
        createMetricObject(key, allMetrics)
      ),
    }
  }, [allMetrics])

  // Get ALL possible metrics for non-exclusive groups (for edit mode)
  const allPossibleNonExclusiveMetrics =
    useMemo((): AllPossibleNonExclusiveMetrics => {
      const getKeysForCategory = (...cats: string[]) =>
        Object.keys(METRIC_DEFINITIONS).filter((key) => {
          const def = METRIC_DEFINITIONS[key]
          return (
            def && cats.includes(def.category) && !HEADER_METRICS.includes(key)
          )
        })

      return {
        heights: getKeysForCategory('height').map((key) =>
          createMetricObject(key, allMetrics)
        ),
        floors: getKeysForCategory('floor').map((key) =>
          createMetricObject(key, allMetrics)
        ),
        setbacks: getKeysForCategory('setback').map((key) =>
          createMetricObject(key, allMetrics)
        ),
        usage: getKeysForCategory('usage').map((key) =>
          createMetricObject(key, allMetrics)
        ),
        environmental: getKeysForCategory('environmental').map((key) =>
          createMetricObject(key, allMetrics)
        ),
        other: getKeysForCategory('other').map((key) =>
          createMetricObject(key, allMetrics)
        ),
      }
    }, [allMetrics])

  return {
    allMetrics,
    hasMetrics,
    metricsByCategory,
    allPossibleMetrics,
    allPossibleNonExclusiveMetrics,
  }
}
